import type { BillingController } from "../../../../api/src/billing/billing.controller";
import type { HouseholdGroupStatement } from "../../../../api/src/billing/billing.service";
import type { HouseholdGroupsController } from "../../../../api/src/household-groups/household-groups.controller";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import type {
  AddHouseholdGroupMemberRequest,
  ChangeHouseholdGroupMembershipRequest,
  CreateHouseholdGroupRequest,
  DesignatePayerAccountRequest,
  DissolveHouseholdGroupRequest,
  HouseholdGroupResponse,
  RemoveHouseholdGroupMemberRequest,
  UpdateHouseholdGroupRequest,
} from "../../../../api/src/household-groups/household-groups.service";
import type { UnpackResponse } from "../../../../api/src/lib";
import { NotificationContext } from "../../Notifications";
import {
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../../lib/api";

/**
 * Returns the function that shows the success message of a write on a
 * household group's page: the same message after each write.
 */
export function useNotifyHouseholdGroupUpdated(
  householdGroupId: number,
  name: string,
) {
  const notificationContext = useContext(NotificationContext);

  return () =>
    notificationContext.pushNotification({
      id: `household-group-${householdGroupId}`,
      header: "Household Group Updated",
      body: `${name} updated`,
      variant: "success",
    });
}

/** Every household group of the firm, dissolved ones included (`GET /household-groups`). */
export function useQueryHouseholdGroups() {
  return useAuthenticatedQuery<
    UnpackResponse<HouseholdGroupsController["list"]>,
    HouseholdGroupResponse[],
    [string]
  >({
    queryKey: ["household-groups"],
    queryFn: async (response) =>
      (response.data ?? []).map((group) => ({
        ...group,
        members: group.members ?? [],
      })),
  });
}

/**
 * One household group with every membership row, ended ones included
 * (`GET /household-groups/:householdGroupId`).
 */
export function useQueryHouseholdGroup(id: number) {
  return useAuthenticatedQuery<
    UnpackResponse<HouseholdGroupsController["getOne"]>,
    HouseholdGroupResponse,
    [string, number]
  >({
    queryKey: ["household-groups", id],
    queryFn: async (response) => ({
      ...response.data,
      members: response.data.members ?? [],
    }),
  });
}

/**
 * The billing reports that hold a group billing statement for a household
 * group, newest end date first
 * (`GET /billing/household-groups/:householdGroupId/statements`).
 */
export function useQueryHouseholdGroupStatements(id: number) {
  return useAuthenticatedQuery<
    UnpackResponse<BillingController["getHouseholdGroupStatements"]>,
    HouseholdGroupStatement[],
    [string, number, string]
  >({
    queryKey: ["household-groups", id, "statements"],
    urlBuilderFn: ([, householdGroupId]) =>
      `/billing/household-groups/${householdGroupId}/statements`,
    queryFn: async (response) => response.data ?? [],
  });
}

/**
 * Creates a household group with its first two or more households
 * (`POST /household-groups`), then refetches the list of groups.
 */
export function useCreateHouseholdGroup() {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["create"]>,
    UnpackResponse<HouseholdGroupsController["create"]>["data"],
    CreateHouseholdGroupRequest
  >({
    mutationKey: ["POST", "household-groups"],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}

/**
 * Adds a household to a group from an effective date
 * (`POST /household-groups/:householdGroupId/members`), then refetches every
 * household group query, the group's own included.
 */
export function useAddHouseholdGroupMember(householdGroupId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["addMember"]>,
    UnpackResponse<HouseholdGroupsController["addMember"]>["data"],
    AddHouseholdGroupMemberRequest
  >({
    mutationKey: ["POST", "household-groups", householdGroupId, "members"],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}

/**
 * Removes a household from a group by writing the end date of its membership
 * with no end date
 * (`POST /household-groups/:householdGroupId/members/:householdId/remove`),
 * then refetches every household group query. `endDate` is inclusive and is
 * sent as entered.
 */
export function useRemoveHouseholdGroupMember(
  householdGroupId: number,
  householdId: number,
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["removeMember"]>,
    UnpackResponse<HouseholdGroupsController["removeMember"]>["data"],
    RemoveHouseholdGroupMemberRequest
  >({
    mutationKey: [
      "POST",
      "household-groups",
      householdGroupId,
      "members",
      householdId,
      "remove",
    ],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}

/**
 * Changes the effective date of a household's membership of a group with no
 * end date (`PATCH /household-groups/:householdGroupId/members/:householdId`),
 * then refetches every household group query.
 */
export function useChangeHouseholdGroupMemberEffectiveDate(
  householdGroupId: number,
  householdId: number,
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["changeMembership"]>,
    UnpackResponse<HouseholdGroupsController["changeMembership"]>["data"],
    ChangeHouseholdGroupMembershipRequest
  >({
    mutationKey: [
      "PATCH",
      "household-groups",
      householdGroupId,
      "members",
      householdId,
    ],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}

/**
 * Changes a household group's name, fee structure or billing minimum
 * (`PATCH /household-groups/:householdGroupId`), then refetches every household
 * group query. A key absent from the body leaves its value unchanged.
 */
export function useUpdateHouseholdGroup(householdGroupId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["update"]>,
    UnpackResponse<HouseholdGroupsController["update"]>["data"],
    UpdateHouseholdGroupRequest
  >({
    mutationKey: ["PATCH", "household-groups", householdGroupId],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}

/**
 * Dissolves a household group from an effective date
 * (`POST /household-groups/:householdGroupId/dissolve`), then refetches every
 * household group query. The api ends the memberships and clears the payer
 * account in the same transaction.
 */
export function useDissolveHouseholdGroup(householdGroupId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["dissolve"]>,
    UnpackResponse<HouseholdGroupsController["dissolve"]>["data"],
    DissolveHouseholdGroupRequest
  >({
    mutationKey: ["POST", "household-groups", householdGroupId, "dissolve"],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}

/**
 * Designates an account of a member household as the group's payer account
 * (`PUT /household-groups/:householdGroupId/payer-account`), then refetches
 * every household group query.
 */
export function useDesignateHouseholdGroupPayer(householdGroupId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["designatePayerAccount"]>,
    UnpackResponse<HouseholdGroupsController["designatePayerAccount"]>["data"],
    DesignatePayerAccountRequest
  >({
    mutationKey: ["PUT", "household-groups", householdGroupId, "payer-account"],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}

/**
 * Clears the group's payer account, so that no payer account is designated
 * (`DELETE /household-groups/:householdGroupId/payer-account`, no body), then
 * refetches every household group query.
 */
export function useClearHouseholdGroupPayer(householdGroupId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdGroupsController["clearPayerAccount"]>,
    UnpackResponse<HouseholdGroupsController["clearPayerAccount"]>["data"],
    void
  >({
    mutationKey: [
      "DELETE",
      "household-groups",
      householdGroupId,
      "payer-account",
    ],
    mutationFn: ({ data }) => data,
    mutationOptions: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["household-groups"] }),
    },
  });
}
